import { useEffect } from 'react'
import { useNavigate, useLocation } from '@tanstack/react-router'
import { useAuth } from '@/contexts/AuthContext'
import { AUTH_RETURN_URL_KEY } from '@/constants/auth'
import { setLocalStorage } from '@/lib/isomorphic'

interface UseRequireAuthOptions {
  /** Extra search params forwarded to /login (e.g. { from: 'openclaw' }) */
  loginSearch?: Record<string, string>
}

/**
 * Redirect to /login when the user is not authenticated.
 *
 * Works reliably for both client-side navigations and full-page loads (SSR
 * hydration), unlike a `beforeLoad` guard which is skipped when
 * `typeof window === 'undefined'`.
 *
 * Usage:
 *   const { isAuthenticated } = useRequireAuth()
 *   if (!isAuthenticated) return null // or a loading spinner
 */
export function useRequireAuth(options?: UseRequireAuthOptions) {
  const { isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    if (!isAuthenticated) {
      // Only save the return URL when we're not already on /login – the
      // navigate() call below triggers a location change that re-fires this
      // effect, and without this guard the stored return URL would be
      // overwritten with "/login", breaking the post-login redirect.
      if (location.pathname !== '/login') {
        setLocalStorage(AUTH_RETURN_URL_KEY, location.pathname + location.searchStr)
      }
      navigate({ to: '/login', search: options?.loginSearch, replace: true })
    }
  }, [isAuthenticated, navigate, location])

  return { isAuthenticated }
}
